import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, of } from 'rxjs';

import { environment } from '../../../environments/environment';
import { apiConstants } from '../constants/api.constants';

// ── Runtime configuration types ───────────────────────────────────────────

export interface AppRuntimeConfig {
  appName: string;
  apiBaseUrl: string;
  featureFlags: Record<string, boolean>;
  buildVersion: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// ConfigService
//
// Two responsibilities:
//
//   1. Bootstrap loader — called once via APP_INITIALIZER (see CoreModule).
//      In production, fetches /assets/config.json so deployments can change
//      runtime settings without a rebuild. Falls back to environment values.
//
//   2. REST request builder — constructs fully-resolved { url, method, body,
//      headers } objects from the apiConstants catalogue, handling path params
//      (`:id` tokens) and URL query strings transparently.
// ─────────────────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class ConfigService {

  private config: AppRuntimeConfig | null = null;

  constructor(private readonly http: HttpClient) {}

  // ── Bootstrap / APP_INITIALIZER API ──────────────────────────────────────

  async load(): Promise<AppRuntimeConfig> {
    if (this.config) return this.config;

    const fallback: AppRuntimeConfig = {
      appName:     environment.appName,
      apiBaseUrl:  environment.apiBaseUrl,
      featureFlags: {
        useMockData: environment.useMockData,
        encryption:  environment.encryptionEnabled,
      },
      buildVersion: '1.0.0',
    };

    try {
      const remote = environment.production
        ? await firstValueFrom(this.http.get<AppRuntimeConfig>('/assets/config.json'))
        : await firstValueFrom(of(fallback));
      this.config = { ...fallback, ...remote };
    } catch {
      this.config = fallback;
    }
    return this.config;
  }

  get<K extends keyof AppRuntimeConfig>(key: K): AppRuntimeConfig[K] | undefined {
    return this.config?.[key];
  }

  get snapshot(): AppRuntimeConfig | null {
    return this.config;
  }

  // ── REST request builder API ──────────────────────────────────────────────

  /**
   * Builds a ready-to-execute REST resource object from the `apiConstants` catalogue.
   *
   * @param moduleName  Top-level key in `apiConstants` (e.g. `'users'`)
   * @param callName    Second-level key                 (e.g. `'detail'`)
   * @param paramsAndBody
   *   - `params`    — path-param replacements: `{ id: '123' }` replaces `:id` in the URL
   *   - `urlParams` — appended as `?key=value` query string pairs
   *   - `body`      — request body (POST / PUT / PATCH)
   *   - `headers`   — per-call header overrides
   *
   * @returns `{ url, method, body, headers }` ready for `RestService.callServer`.
   *
   * @example
   * const resource = configSvc.getRestObject('users', 'detail', { params: { id: '42' } });
   * // → { url: '/api/users/42', method: 'GET', body: {}, headers: {} }
   */
  getRestObject(moduleName: string, callName: string, paramsAndBody?: any): any {
    const resourceObject = apiConstants[moduleName]?.[callName];
    if (!resourceObject) {
      throw new Error(`apiConstants['${moduleName}']['${callName}'] is not defined.`);
    }

    const constructedUrl =
      paramsAndBody && (!!paramsAndBody['params'] || !!paramsAndBody['urlParams'])
        ? this.constructUrl(resourceObject, paramsAndBody)
        : resourceObject['url'];

    return {
      url:     constructedUrl,
      method:  resourceObject['method'],
      body:    paramsAndBody?.body    ?? {},
      headers: paramsAndBody?.headers ?? {},
    };
  }

  // ── Private URL construction helpers ─────────────────────────────────────

  private constructUrl(resource: { url: string }, paramsAndBody: any): string {
    const splitUrl = resource['url'].split('/');

    if (paramsAndBody['params']) {
      for (const key in paramsAndBody['params']) {
        const plainIndex  = splitUrl.indexOf(key);
        const colonIndex  = splitUrl.indexOf(`:${key}`);
        if (plainIndex > -1) {
          splitUrl[plainIndex] = paramsAndBody['params'][key];
        } else if (colonIndex > -1) {
          splitUrl[colonIndex] = paramsAndBody['params'][key];
        }
      }
    }

    return splitUrl.join('/') + this.constructQueryString(paramsAndBody['urlParams']);
  }

  private constructQueryString(urlParamsObject: any): string {
    if (!urlParamsObject || Object.keys(urlParamsObject).length === 0) return '';
    const parts: string[] = [];
    for (const key in urlParamsObject) {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(urlParamsObject[key])}`);
    }
    return '?' + parts.join('&');
  }
}
